import {
  DataObject,
  ObjectPermission,
  Post,
  User,
} from '../models/core.models';
import { DestroyRef, Injectable, inject } from '@angular/core';
import {
  DialogCloseEvent,
  DialogCloseOptions,
  DialogCloseReason,
  DialogService,
} from '@sui/dialog';
import { NomAlgorithm, NomObjectClass } from '../constants';
import {
  applyFeedPostAlgorithm,
  updateFeedAlgorithmParams,
} from '../actions/feed.actions';
import {
  applyPostAlgorithm,
  applySelectedPostsAlgorithm,
} from '../actions/post.actions';
import { catchError, from, of, switchMap } from 'rxjs';

import { AlghorithmDialogComponent } from '../components/popups/alghorithm-dialog/alghorithm-dialog.component';
import { AppFeatures } from '../app.features';
import { AppState } from 'src/app/reducers';
import { AutoStoryToolDialogComponent } from 'src/app/shared/components/auto-story-tool-dialog/auto-story-tool-dialog.component';
import { ConfirmDialogComponent } from '../components/popups/confirm-dialog/confirm-dialog.component';
import { FeedAlgorithm } from '../models/algorithm.models';
import { FeedService } from './feed.service';
import { FormProperty } from '../models/form.models';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { LinkRelatedArticlesDialogComponent } from 'src/app/shared/components/link-related-articles-dialog/link-related-articles-dialog.component';
import { LoggerService } from '@services/logger.service';
import { PostSentimentDialogComponent } from '../components/popups/post-sentiment-dialog/post-sentiment-dialog.component';
import { PostUserCategoriesDialogComponent } from '../components/popups/post-user-categories-dialog/post-user-categories-dialog.component';
import { ShareDialog } from '../components/popups/share-dialog/share-dialog';
import { ShareMode } from '../components/popups/share-dialog/share-dialog';
import { Store } from '@ngrx/store';
import { ToastService } from '@services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { UserFacadeService } from '@services/user-facade.service';
import { realPostId } from 'src/app/shared/utils/shadow-post.utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface PostAlgorithm {
  id: number;
  name: string;
  icon?: IconProp;
  group?: string;
  applicableFunction: (post: Post) => boolean;
  paramsProvider?: Function;
  permission?: AppFeatures;
  objectPermissions?: ObjectPermission[];
  canSchedule?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class PostAlgorithmService {
  destroyRef = inject(DestroyRef);

  userFacade = inject(UserFacadeService);
  dialogService = inject(DialogService);

  constructor(
    private store: Store<AppState>,
    private translateService: TranslateService,
    private logger: LoggerService,
    private feedService: FeedService,
    private toastService: ToastService,
  ) {}

  algorithms: PostAlgorithm[] = [
    {
      id: NomAlgorithm.SHARE,
      name: this.translateService.instant('feed-actions.button.share'),
      icon: ['fas', 'share'],
      group: 'export',
      applicableFunction: (post: Post) => true,
      permission: AppFeatures.EXPORT_AND_SHARE_ROOT,
      objectPermissions: ['can_view'],
      paramsProvider: (params: any) => {
        const shareMode = params.shareMode;

        return new Promise((resolve, reject) => {
          // * Here we have 3 invocation cases:
          // * 1. shareMode: 'selected' - no need to pass postIds! BE does not need postIds, only feedId - BE keeps the persistent state of user-selected postIds anyway.
          // * 2. shareMode: 'resultset' - NO need to pass postIds! BE does not need postIds, only feedId.
          // * 3. shareMode: 'single' - we HAVE to pass single postId as a single array element! BE needs the data.

          let data = { shareMode };

          if (shareMode === ShareMode.SINGLE) {
            data['postIds'] = [params.postId];
          }

          const dialogRef = this.dialogService.openTabsDialog(ShareDialog, {
            data,
            closeOnClick: false,
            closeOnEscape: true,
            width: '25%',
            height: '60%',
            minWidth: 415,
            minHeight: 800,
            maxWidth: 1020,
            maxHeight: 1020,
          });

          dialogRef.closed.subscribe((result) => {
            if (
              result.reason === DialogCloseReason.Save ||
              result.reason === DialogCloseReason.SaveAndClose
            ) {
              resolve({});
            } else {
              reject();
            }
          });
        });
      },
      //action: () => this.exportSelectedItems('word'),
    },
    {
      id: NomAlgorithm.SET_SENTIMENT,
      name: this.translateService.instant('post-algorithm.title.set-sentiment'),
      icon: ['fas', 'face-smile'],
      group: 'main',
      objectPermissions: ['can_view', 'can_edit'], // needs edit permission?
      permission: AppFeatures.ADVANCED_MASS_OPERATIONS_EDIT_SENTIMENT,
      applicableFunction: (post: Post) => {
        // Implement the logic for setting sentiment
        return true; // Replace with actual condition
      },
      paramsProvider: (params: any) => {
        return new Promise((resolve, reject) => {
          const dialogRef = this.dialogService.openTabsDialog(
            PostSentimentDialogComponent,
            {
              data: {
                postId: params.postId,
              },
              closeOnClick: false,
              closeOnEscape: true,
              width: '30vw',
              height: '30vh',
              // minWidth: 800,
              // minHeight: 800,
              // maxWidth: 1020,
              // maxHeight: 1020,
            },
          );

          dialogRef.closed.subscribe((result) => {
            if (
              result.reason === DialogCloseReason.Save ||
              result.reason === DialogCloseReason.SaveAndClose
            ) {
              resolve({
                sentiment_id: result.data['sentimentId'],
              });
            }
          });
        });
      },
    },
    {
      id: NomAlgorithm.SET_CATEGORIES,
      name: this.translateService.instant(
        'post-algorithm.title.set-user-categories',
      ),
      icon: ['fas', 'tag'],
      group: 'main',
      permission: AppFeatures.ADVANCED_MASS_OPERATIONS_SET_CATEGORIES,
      objectPermissions: ['can_view'],
      applicableFunction: (post: Post) => {
        // Implement the logic for setting user categories
        return true; // Replace with actual condition
      },
      paramsProvider: (params: any) => {
        return new Promise((resolve, reject) => {
          const dialogRef = this.dialogService.openTabsDialog(
            PostUserCategoriesDialogComponent,
            {
              data: {
                postIds: [params.postId],
                selectedPostsFeedId:
                  typeof params.feedId !== 'undefined' ? params.feedId : null,
                validateFormsOnInit: true,
              },
              closeOnClick: false,
              closeOnEscape: true,
              width: '50vw',
              height: '50vh',
              minWidth: 800,
              minHeight: 800,
              maxWidth: 1020,
              maxHeight: 1020,
            },
          );

          dialogRef.closed.subscribe((event: DialogCloseEvent<unknown>) => {
            if (
              event.reason === DialogCloseReason.Save ||
              event.reason === DialogCloseReason.SaveAndClose
            ) {
              resolve({
                categories: event.data['categories'],
                update_operation: event.data['updateOperationId'],
              }); // Resolve the promise with the data
            }
          });
        });
      },
    },
    {
      id: NomAlgorithm.GENERATE_SCREEN_CAPTURE,
      name: this.translateService.instant(
        'post-algorithm.title.execute-screen-capture',
      ),
      icon: ['fas', 'camera'],
      group: 'user',
      permission: AppFeatures.ADVANCED_MASS_OPERATIONS_SCREEN_CAPTURE,
      objectPermissions: ['can_view'],
      applicableFunction: (post: Post) => {
        // Implement the logic for executing screen capture
        return true; // Replace with actual condition
      },
      paramsProvider: this.genericParamsProvider(
        NomAlgorithm.GENERATE_SCREEN_CAPTURE,
        this.translateService.instant(
          'post-algorithm.title.execute-screen-capture',
        ),
        'post-algorithm.execute-screen-capture.confirm-message',
      ),
      canSchedule: true,
    },
    {
      id: NomAlgorithm.POST_FACE_DETECTION,
      name: this.translateService.instant(
        'post-algorithm.title.execute-face-recognition',
      ),
      icon: ['fas', 'user'],
      objectPermissions: ['can_view'],
      group: 'user',
      permission: AppFeatures.ADVANCED_MASS_OPERATIONS_FACE_DETECTION,
      applicableFunction: (post: Post) => {
        // Implement the logic for executing face detection
        return true; // Replace with actual condition
      },
      paramsProvider: this.genericParamsProvider(
        NomAlgorithm.POST_FACE_DETECTION,
        this.translateService.instant(
          'post-algorithm.title.execute-face-recognition',
        ),
        'post-algorithm.execute-face-recognition.confirm-message',
      ),
      canSchedule: true,
    },
    {
      id: NomAlgorithm.POST_HARVEST,
      name: this.translateService.instant(
        'post-algorithm.title.reharvest-content',
      ),
      icon: ['fas', 'rss'],
      group: 'system',
      permission: AppFeatures.ADVANCED_MASS_OPERATIONS_RE_HARVEST,
      objectPermissions: ['can_view'],
      applicableFunction: (post: Post) => {
        // Implement the logic for reharvesting content
        return true; // Replace with actual condition
      },
      paramsProvider: this.genericConfirmParamsProvider(
        'post-algorithm.confirm.reharvest-content-message',
      ),
    },
    {
      id: NomAlgorithm.POST_UPDATE_SOURCE_FAVICON,
      name: this.translateService.instant(
        'post-algorithm.title.update-source-favicon',
      ),
      icon: ['fas', 'image'],
      group: 'system',
      permission: AppFeatures.ADVANCED_MASS_OPERATIONS_RE_HARVEST,
      objectPermissions: ['can_view'],
      applicableFunction: (post: Post) => true,
      paramsProvider: this.genericConfirmParamsProvider(
        'post-algorithm.update-source-favicon.confirm-message',
      ),
    },
    {
      id: NomAlgorithm.POST_REINDEX,
      name: this.translateService.instant(
        'post-algorithm.title.reindex-content',
      ),
      icon: ['fas', 'tag'],
      group: 'system',
      permission: AppFeatures.ADVANCED_MASS_OPERATIONS_RE_INDEX,
      objectPermissions: ['can_view'],
      applicableFunction: (post: Post) => {
        // Implement the logic for reindexing content
        return true; // Replace with actual condition
      },
      paramsProvider: this.genericConfirmParamsProvider(
        'post-algorithm.reindex-content.confirm-message',
      ),
    },
    {
      id: NomAlgorithm.POST_LANGUAGE_DETECTION,
      name: this.translateService.instant(
        'post-algorithm.title.detect-language',
      ),
      icon: ['fas', 'language'],
      group: 'system',
      permission: AppFeatures.ADVANCED_MASS_OPERATIONS_DETECT_LANGUAGE,
      objectPermissions: ['can_view'],
      applicableFunction: (post: Post) => {
        // Implement the logic for detecting language
        return true; // Replace with actual condition
      },
      paramsProvider: this.genericConfirmParamsProvider(
        'post-algorithm.detect-language.confirm-message',
        NomAlgorithm.POST_LANGUAGE_DETECTION,
      ),

      canSchedule: true,
    },
    {
      id: NomAlgorithm.POST_ESTIMATE_CONTENT_SENTIMENT,
      name: this.translateService.instant(
        'post-algorithm.title.estimate-sentiment',
      ),
      icon: ['fas', 'face-smile'],
      group: 'system',
      permission: AppFeatures.ADVANCED_MASS_OPERATIONS_ESTIMATE_SENTIMENT,
      objectPermissions: ['can_view'],
      applicableFunction: (post: Post) => {
        // Implement the logic for estimating sentiment
        return true; // Replace with actual condition
      },

      paramsProvider: this.genericConfirmParamsProvider(
        'post-algorithm.estimate-sentiment.confirm-message',
      ),
    },
    {
      id: NomAlgorithm.POST_RECONSUME,
      name: this.translateService.instant(
        'post-algorithm.title.recalculate.post-reconsume',
      ),
      icon: ['fas', 'wrench'],
      group: 'system',
      permission: AppFeatures.ADVANCED_MASS_OPERATIONS_RECALCULATE_POST_REACH,
      objectPermissions: ['can_view'],
      applicableFunction: (post: Post) => {
        // Implement the logic for recalculating post reach
        return true; // Replace with actual condition
      },
      paramsProvider: this.genericConfirmParamsProvider(
        'post-algorithm.post-reconsume.confirm-message',
      ),
    },
    {
      id: NomAlgorithm.POST_REACH_REACALCULATE,
      name: this.translateService.instant(
        'post-algorithm.title.recalculate.post-reach',
      ),
      icon: ['fas', 'users'],
      group: 'system',
      permission: AppFeatures.ADVANCED_MASS_OPERATIONS_RECALCULATE_POST_REACH,
      objectPermissions: ['can_view'],
      applicableFunction: (post: Post) => {
        // Implement the logic for recalculating post reach
        return true; // Replace with actual condition
      },
      paramsProvider: this.genericConfirmParamsProvider(
        'post-algorithm.post-reach.confirm-message',
      ),
    },
    {
      id: NomAlgorithm.POST_VIDEOS_SPEECH_TO_TEXT_WHISPER,
      name: this.translateService.instant(
        'post-algorithm.title.run-speech-to-text',
      ),
      icon: ['fas', 'volume-up'],
      group: 'user',
      permission: AppFeatures.ADVANCED_MASS_OPERATIONS_EXECUTE_SPEECH_TO_TEXT,
      objectPermissions: ['can_view'],
      applicableFunction: (post: Post) => {
        // Implement the logic for running speech to text
        return true; // Replace with actual condition
      },
      paramsProvider: this.genericParamsProvider(
        NomAlgorithm.POST_VIDEOS_SPEECH_TO_TEXT_WHISPER,
        this.translateService.instant(
          'post-algorithm.title.run-speech-to-text',
        ),
        'post-algorithm.run-speech-to-text.confirm-message',
      ),
      canSchedule: true,
    },
    {
      id: NomAlgorithm.POST_GENERATE_AI_SYNOPSIS,
      name: this.translateService.instant(
        'post-algorithm.title.generate-ai-sypnosis',
      ),
      icon: ['fas', 'tag'],
      group: 'user',
      applicableFunction: (post: Post) => {
        // Implement the logic for generating AI synopses
        return true; // Replace with actual condition
      },
      permission: AppFeatures.ADVANCED_MASS_OPERATIONS_AI_SYNOPSIS,
      objectPermissions: ['can_view'],
      paramsProvider: this.genericParamsProvider(
        NomAlgorithm.POST_GENERATE_AI_SYNOPSIS,
        this.translateService.instant(
          'post-algorithm.title.generate-ai-sypnosis',
        ),
        'post-algorithm.title.confirm-message',
      ),
    },
    {
      id: NomAlgorithm.POST_TRANSLATE,
      name: this.translateService.instant(
        'post-algorithm.title.translate-post',
      ),
      icon: ['fas', 'language'],
      group: 'user',
      applicableFunction: (post: Post) => {
        // Implement the logic for translating posts
        return true; // Replace with actual condition
      },
      permission: AppFeatures.ADVANCED_MASS_OPERATIONS_TRANSLATION,
      objectPermissions: ['can_view'],
      paramsProvider: this.genericParamsProvider(
        NomAlgorithm.POST_TRANSLATE,
        this.translateService.instant('post-algorithm.title.translate-post'),
        'post-algorithm.title.confirm-message',
      ),
      canSchedule: true,
    },
    {
      id: NomAlgorithm.POST_RELATED_CONTENT_EMBEDDINGS,
      name: this.translateService.instant(
        'post-algorithm.title.index-related-content',
      ),
      icon: ['fas', 'tag'],
      group: 'system',
      permission: AppFeatures.FEED_SEARCH_RESULTS_LINK_RELATED_CONTENT_MANUALLY,
      objectPermissions: ['can_view'],
      applicableFunction: (post: Post) => true,
      paramsProvider: this.genericConfirmParamsProvider(
        'post-algorithm.index-related-content.confirm-message',
        NomAlgorithm.POST_RELATED_CONTENT_EMBEDDINGS,
      ),
      canSchedule: true,
    },
    {
      id: NomAlgorithm.POST_RELATED_CONTENT_LINK_SIMILAR,
      name: this.translateService.instant(
        'post-algorithm.title.link-related-articles',
      ),
      icon: ['fas', 'link'],
      group: 'system',
      permission: AppFeatures.FEED_SEARCH_RESULTS_LINK_RELATED_CONTENT_MANUALLY,
      objectPermissions: ['can_view'],
      applicableFunction: (post: Post) => true,
      paramsProvider: (params: any) => {
        return new Promise((resolve, reject) => {
          const algorithm = this.algorithms.find(
            (alg) => alg.id === NomAlgorithm.POST_RELATED_CONTENT_LINK_SIMILAR,
          );
          const isResultset = params.shareMode === ShareMode.RESULTSET;
          const canSchedule =
            isResultset && !!algorithm?.canSchedule && !params.editMode;

          const dialogRef = this.dialogService.openSimpleDialog(
            LinkRelatedArticlesDialogComponent,
            {
              data: {
                canSchedule,
                editMode: !!params.editMode,
                initialPayload: params.initialValues,
              },
              closeOnClick: false,
              closeOnEscape: true,
            },
          );

          dialogRef.closed.subscribe(
            (result: DialogCloseOptions<{ payload: any } | undefined>) => {
              if (
                result.reason === DialogCloseReason.Save ||
                result.reason === DialogCloseReason.SaveAndClose
              ) {
                const payload = result.data?.payload;
                if (!payload) {
                  resolve(payload);
                  return;
                }
                const { current_feed_only, ...rest } = payload;
                resolve({
                  ...rest,
                  feed_id: current_feed_only ? params.feedId : null,
                });
              } else {
                reject();
              }
            },
          );
        });
      },
      canSchedule: true,
    },
    {
      id: NomAlgorithm.POST_RELATED_CONTENT_UNLINK,
      name: this.translateService.instant(
        'post-algorithm.title.unlink-related-content',
      ),
      icon: ['fas', 'link-slash'],
      group: 'system',
      permission: AppFeatures.FEED_SEARCH_RESULTS_LINK_RELATED_CONTENT_MANUALLY,
      objectPermissions: ['can_view'],
      applicableFunction: (post: Post) => true,
      paramsProvider: this.genericConfirmParamsProvider(
        'post-algorithm.unlink-related-content.confirm-message',
      ),
    },
    {
      id: NomAlgorithm.QUERY_AUTO_STORY,
      name: this.translateService.instant(
        'post-algorithm.title.run-auto-story-tool',
      ),
      icon: ['fas', 'face-smile'],
      group: 'user',
      permission: AppFeatures.ADVANCED_MASS_OPERATIONS_AI_AUTO_STORY,
      applicableFunction: (post: Post) => true,
      paramsProvider: (params: any) => {
        return new Promise((resolve, reject) => {
          const algorithm = this.algorithms.find(
            (alg) => alg.id === NomAlgorithm.QUERY_AUTO_STORY,
          );
          const canSchedule =
            params.shareMode === ShareMode.RESULTSET &&
            !!algorithm?.canSchedule &&
            !params.editMode;
          const dialogRef = this.dialogService.openSimpleDialog(
            AutoStoryToolDialogComponent,
            {
              data: {
                canSchedule,
                editMode: !!params.editMode,
                initialPayload: params.initialValues,
              },
              closeOnClick: false,
              closeOnEscape: true,
            },
          );

          dialogRef.closed.subscribe(
            (result: DialogCloseOptions<{ payload: any } | undefined>) => {
              if (
                result.reason === DialogCloseReason.Save ||
                result.reason === DialogCloseReason.SaveAndClose
              ) {
                resolve(result.data?.payload);
              } else {
                reject();
              }
            },
          );
        });
      },
      canSchedule: false,
    },
  ];

  private async resolveParams(
    algorithm: PostAlgorithm,
    paramsProviderParams: any,
  ): Promise<any | null> {
    if (!algorithm.paramsProvider) {
      return {};
    }
    try {
      const result = await algorithm.paramsProvider(paramsProviderParams);
      return result ?? null;
    } catch (err) {
      this.logger.error('Error in paramsProvider:', err);
      return null;
    }
  }

  genericConfirmParamsProvider(
    confirmMessageKey: string,
    algorithmId?: number,
  ) {
    return (params: any) => {
      return new Promise((resolve, reject) => {
        if (params.shareMode === ShareMode.SINGLE) {
          // no confirmation for single post actions
          resolve({});
        } else {
          this.logger.debug('genericConfirmParamsProvider: ', params);
          let scopeKey = '';

          if (params.shareMode === ShareMode.RESULTSET) {
            scopeKey = 'post-algorithm.scope-resultset.confirm-message';
          } else if (params.shareMode === ShareMode.SELECTED) {
            scopeKey = 'post-algorithm.scope-selected.confirm-message';
          }

          this.logger.debug('confirmMessageKey: ', confirmMessageKey);

          const message =
            this.translateService.instant(confirmMessageKey) +
            ' ' +
            this.translateService.instant(scopeKey, {
              item_count: params.itemCount || 0,
            });

          const algorithm =
            algorithmId !== undefined
              ? this.algorithms.find((alg) => alg.id === algorithmId)
              : undefined;
          const isResultset = params.shareMode === ShareMode.RESULTSET;
          const canSchedule = isResultset && !!algorithm?.canSchedule;

          const dialogRef = this.dialogService.openSimpleDialog(
            ConfirmDialogComponent,
            {
              data: {
                message,
                canSchedule,
                initialSchedule: !!params.schedule,
                editMode: !!params.editMode,
                initialValues: params.initialValues,
              },
              closeOnClick: false,
            },
          );

          dialogRef.closed.subscribe((event: DialogCloseOptions<any>) => {
            if (event.reason === DialogCloseReason.Confirm) {
              resolve(event.data ?? {});
            } else if (event.reason === DialogCloseReason.Cancel) {
              this.logger.debug(`Action cancelled: ${event.reason}`);
              reject(event.reason);
            }
          });
        }
      });
    };
  }

  genericParamsProvider(
    algorithmId: number,
    title: string,
    confirmMessageKey: string,
  ): (params: any) => Promise<any> {
    return (params: any) => {
      return new Promise((resolve, reject) => {
        const algorithm = this.algorithms.find((alg) => alg.id === algorithmId);
        const isResultset = params.shareMode === ShareMode.RESULTSET;
        const canSchedule =
          isResultset && !!algorithm?.canSchedule && !params.editMode;

        this.feedService
          .fetchAlgorithFormParams(algorithmId)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            switchMap((formProps: FormProperty[]) => {
              const finalProps = formProps ?? [];

              if (finalProps.length > 0 || canSchedule || params.editMode) {
                const dialogRef = this.dialogService.openSimpleDialog(
                  AlghorithmDialogComponent,
                  {
                    data: {
                      algorithmId,
                      title,
                      formProperties: finalProps,
                      canSchedule,
                      initialSchedule: !!params.schedule,
                      initialValues: params.initialValues,
                      editMode: !!params.editMode,
                    },
                    closeOnEscape: true,
                    closeOnClick: false,
                    width: '20vw',
                    height: '30vh',
                    minWidth: 400,
                    minHeight: 600,
                    maxWidth: 800,
                    maxHeight: 1020,
                  },
                );

                return dialogRef.closed;
              } else {
                return from(
                  this.genericConfirmParamsProvider(confirmMessageKey)(params),
                );
              }
            }),
            catchError((err) => {
              console.error('Error in genericParamsProvider', err);
              reject(err);
              return of(null); // complete the stream gracefully
            }),
          )
          .subscribe({
            next: (result) => {
              if (result && typeof result === 'object' && 'reason' in result) {
                const closeEvent = result as DialogCloseEvent<any>;
                // * dialog closed with a reason - check if it was Save or SaveAndClose
                if (
                  closeEvent.reason === DialogCloseReason.Save ||
                  closeEvent.reason === DialogCloseReason.SaveAndClose
                ) {
                  resolve(closeEvent.data);
                } else {
                  // * Cancel, Escape, Backdrop, DiscardChanges - abort algorithm
                  resolve(null);
                }
              } else {
                // * fallback for non-dialog results (genericConfirmParamsProvider)
                resolve(result);
              }
            },
            error: (err) => reject(err),
          });
      });
    };
  }

  /**
   * Runs a post algorithm on a given post with the specified algorithm ID and input parameters.
   * If the algorithm has a paramsProvider function, it will be called to retrieve the algorithm parameters.
   * The algorithm parameters will then be applied to the post using the applyPostAlgorithm action.
   *
   * @param post The post to run the algorithm on.
   * @param algorithmId The ID of the algorithm to run.
   * @param paramsProviderParams The input parameters for the algorithm.
   */
  runPostAlgorithm(
    feedId: string,
    post: Post,
    algorithmId: number,
    paramsProviderParams: any = {},
    // scheduling is RESULTSET-only; this flag never surfaces a schedule UI here
    schedule: boolean = false,
  ) {
    const algorithm = this.algorithms.find((alg) => alg.id === algorithmId);
    if (!algorithm) return;

    // The caller may hand us a display-only thread "shadow" copy; the backend
    // only knows the real post id.
    const postId = realPostId(post.id);

    if (!paramsProviderParams.postId) {
      paramsProviderParams.postId = postId;
      paramsProviderParams.shareMode = ShareMode.SINGLE;
    }
    paramsProviderParams.schedule = schedule;

    this.resolveParams(algorithm, paramsProviderParams).then(
      (algorithmParams) => {
        if (algorithmParams === null) return;
        this.store.dispatch(
          applyPostAlgorithm({
            feedId,
            postId,
            algorithmId,
            algorithmParams,
          }),
        );
      },
    );
  }

  /**
   * Runs an algorithm for feed selected posts
   *
   * @param feedId - The ID of the feed.
   * @param algorithmId - The ID of the algorithm.
   * @param paramsProviderParams - Additional parameters for the params provider function.
   */
  runPostAlgorithmSelected(
    feedId: string,
    algorithmId: number,
    itemCount: number,
    paramsProviderParams: any = {},
    // scheduling is RESULTSET-only; this flag never surfaces a schedule UI here
    schedule: boolean = false,
  ) {
    const algorithm = this.algorithms.find((alg) => alg.id === algorithmId);
    if (!algorithm) return;

    if (!paramsProviderParams.postId) {
      paramsProviderParams.feedId = feedId;
      paramsProviderParams.shareMode = ShareMode.SELECTED;
      paramsProviderParams.itemCount = itemCount;
    }
    paramsProviderParams.schedule = schedule;

    this.resolveParams(algorithm, paramsProviderParams).then(
      (algorithmParams) => {
        if (algorithmParams === null) return;
        this.store.dispatch(
          applySelectedPostsAlgorithm({
            feedId,
            algorithmId,
            algorithmParams,
          }),
        );
      },
    );
  }

  /**
   * Runs the post algorithm for a specific feed.
   *
   * @param feedId - The ID of the feed.
   * @param algorithmId - The ID of the algorithm.
   * @param paramsProviderParams - Additional parameters for the params provider function.
   * @returns A Promise that resolves when the post algorithm is completed.
   */
  runPostAlgorithmFeed(
    feedId: string,
    algorithmId: number,
    itemCount: number,
    paramsProviderParams: any = {},
    schedule: boolean = false,
  ) {
    const algorithm = this.algorithms.find((alg) => alg.id === algorithmId);
    if (!algorithm) return;

    if (!paramsProviderParams.postId) {
      paramsProviderParams.feedId = feedId;
      paramsProviderParams.shareMode = ShareMode.RESULTSET;
      paramsProviderParams.itemCount = itemCount;
    }
    paramsProviderParams.schedule = schedule;

    this.resolveParams(algorithm, paramsProviderParams).then(
      (algorithmParams) => {
        if (algorithmParams === null) return;
        this.store.dispatch(
          applyFeedPostAlgorithm({
            feedId,
            algorithmId,
            algorithmParams,
          }),
        );
      },
    );
  }

  /**
   * Opens the algorithm's paramsProvider pre-filled with the currently
   * configured params for an attached feed algorithm, then dispatches an
   * update action with the user's edits.
   */
  editFeedAlgorithmParams(feedId: string, feedAlgorithm: FeedAlgorithm) {
    const algorithm = this.algorithms.find(
      (alg) => alg.id === feedAlgorithm.id,
    );
    if (!algorithm || !algorithm.paramsProvider) return;

    const existingParams =
      feedAlgorithm.params && !Array.isArray(feedAlgorithm.params)
        ? (feedAlgorithm.params as Record<string, any>)
        : {};

    const initialValues = {
      ...existingParams,
      ...(typeof feedAlgorithm.delay_interval_minutes === 'number'
        ? { schedule_interval_minutes: feedAlgorithm.delay_interval_minutes }
        : {}),
      ...(feedAlgorithm.recurrence
        ? { schedule_recurrence: feedAlgorithm.recurrence }
        : {}),
      ...(feedAlgorithm.timezone_id != null
        ? { schedule_timezone_id: feedAlgorithm.timezone_id }
        : {}),
    };

    const paramsProviderParams = {
      feedId,
      shareMode: ShareMode.RESULTSET,
      schedule: false,
      editMode: true,
      initialValues,
    };

    this.resolveParams(algorithm, paramsProviderParams).then((params) => {
      if (params === null) return;
      this.store.dispatch(
        updateFeedAlgorithmParams({
          feedId,
          algorithmId: feedAlgorithm.id,
          params,
          scheduleId: feedAlgorithm.schedule_id,
        }),
      );
    });
  }

  getAlgorithmsPerUser(
    user: User | null | undefined,
    object: DataObject | null | undefined,
  ) {
    if (!user || !user.feature_components) {
      return [];
    }

    return this.algorithms.filter((algorithm) => {
      // must have permission flag and feature enabled
      if (
        !algorithm.permission ||
        !user.feature_components[algorithm.permission]
      ) {
        return false;
      }

      // skip objectPermissions check entirely if no object or if algorithm has none
      if (!algorithm.objectPermissions || !object) {
        return true;
      }

      // otherwise enforce object permissions
      return this.userFacade.hasAllObjectPermissionsSync(
        object,
        algorithm.objectPermissions,
      );
    });
  }
}
